import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  Input,
  OnChanges,
  OnInit,
  Renderer2,
  ViewChild,
} from '@angular/core';
import { ReactiveFormsModule } from '@angular/forms';
import { NgSelectComponent, NgSelectModule } from '@ng-select/ng-select';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { of } from 'rxjs';
import {
  catchError,
  debounceTime,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  tap,
} from 'rxjs/operators';
import { CitiesService } from '@app/services/cities.service';
import { City } from '@app/interfaces/address.interface';
import { CatalogCountry } from '@app/interfaces/country-information.interface';
import { BaseInputComponent } from '@app/shared/forms/base-input.component';

@Component({
  // eslint-disable-next-line @angular-eslint/component-selector
  selector: 'lib-mt-city-picker',
  templateUrl: './mt-city-picker.component.html',
  styleUrls: ['./mt-city-picker.component.less'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, NgSelectModule, TranslateModule],
})
export class MtCityPickerComponent
  extends BaseInputComponent
  implements OnInit, OnChanges, AfterViewInit
{
  @ViewChild('select', { static: true }) select: NgSelectComponent;
  @Input() countryId: number;
  @Input() clearInput?: boolean;
  @Input() noFoundCode: string;
  @Input() loadingText: string;

  currentCity: City;
  isFocused = false;
  isClickOnOption = false;
  indexList = 1;
  pressKey = false;
  currentListElement: CatalogCountry[];
  mouseMoveEvent: Event;
  isMouseMove = true;
  openDropdown = false;

  cities: City[] = [];

  constructor(
    private readonly translateService: TranslateService,
    private render: Renderer2,
    private readonly citiesService: CitiesService
  ) {
    super();
    this.formControlType = 'countrySearchablePicker';
  }

  ngOnInit() {
    this.observeCity();
    this.keyArrowsListener();
    this.observeSearch();
  }

  ngAfterViewInit() {
    if (this.value) {
      this.displayCurrentCityName();
    }
  }

  ngOnChanges() {
    this.load();
  }

  private load() {
    this.currentCitySelect();
  }

  currentCitySelect() {
    this.currentCity = this.value;
    if (this.currentCity) {
      this.currentCity = this.cities.find((x) => x.Id === this.value.Id);
      this.displayCurrentCityName();
    }
  }

  observeCity() {
    this.subscription.add(
      this.control.valueChanges
        .pipe(
          distinctUntilChanged(),
          tap((value) => !value && this.resetName())
        )
        .subscribe({
          next: (res) => {
            this.currentCity = res;
            this.cities = [];
            this.displayCurrentCityName();
            if (this.value) {
              this.indexList = this.cities.findIndex((country) => country.Id === this.value.Id) + 1;
              if (this.indexList === this.cities.length) {
                this.indexList--;
              }
            } else {
              this.indexList = 1;
            }
            this.openDropdown = false;
          },
        })
    );
  }

  displayCurrentCityName(index?: number) {
    this.indexList = index + 1;
    if (this.currentCity) {
      this.isClickOnOption = true;
      const inputSearchRef = this.select.element.getElementsByTagName('input');
      inputSearchRef[0].value = this.currentCity.Name;
    }
  }

  onSearch(e) {
    this.control.patchValue(null);
    this.currentCity = null;
    if (e.term === '') {
      this.cities = [];
      this.select.handleClearClick();
    }
  }

  onBlur() {
    this.onFocus();
    this.openDropdown = false;
  }

  onFocus() {
    this.clearInput = false;
    this.isFocused = !this.isFocused;
  }

  onOptionSelect(index?: number) {
    this.indexList = index + 1;
    this.isClickOnOption = true;
  }

  onOpenList() {
    if (!this.value) {
      this.indexList = 1;
    } else {
      this.indexList = this.cities.findIndex((city) => city.Id === this.value.Id) + 1;
    }
    this.select.focus();
  }

  getPlaceholder(): string {
    return this.translateService.instant(
      this.placeholder ? this.placeholder : 'HT_M_SELECT_CITY_CHARACTERES'
    );
  }

  private keyArrowsListener() {
    const containerRef = this.select.element.getElementsByClassName('ng-select-container')[0];
    this.render.listen(containerRef, 'keydown.arrowdown', (t) => {
      this.pressKey = true;
      this.mouseMoveEvent.preventDefault();
      this.mouseMoveEvent.stopImmediatePropagation();
      if (this.indexList >= this.cities.length) {
        this.stopPropagation(t);
      }
      if (this.indexList < this.cities.length) {
        this.indexList++;
        if (!this.select.isOpen) {
          this.indexList--;
        }
      }
    });
    this.render.listen(containerRef, 'keydown.arrowup', (t) => {
      this.mouseMoveEvent.preventDefault();
      this.mouseMoveEvent.stopImmediatePropagation();
      this.pressKey = true;
      if (this.indexList <= 1) {
        this.stopPropagation(t);
      }
      this.indexList--;
      if (this.indexList === 0) {
        this.indexList = 1;
      }
    });
  }

  private stopPropagation(e: Event) {
    e.stopImmediatePropagation();
    e.preventDefault();
    e.stopPropagation();
  }

  onMouseover(index: number) {
    this.indexList = index + 1;
    this.pressKey = false;
  }

  onMouseMove(e: Event) {
    this.mouseMoveEvent = e;
    this.pressKey = false;
  }

  private observeSearch() {
    this.select.close();
    this.select.searchEvent
      .pipe(
        debounceTime(800),
        distinctUntilChanged(),
        filter((res) => res.term.length >= 3),
        tap(() => (this.loading = true)),
        switchMap((term) =>
          this.citiesService.getCityBySearchTerm(this.countryId, term.term).pipe(
            map((res) => res.Data),
            catchError(() => {
              return of([]);
            })
          )
        ),
        tap(() => (this.loading = false))
      )
      .subscribe({
        next: (res) => {
          if (this.select.itemsList.markedIndex >= 0) {
            this.indexList = this.select.itemsList.markedIndex + 1;
          }
          this.cities = res;
          this.openDropdown = true;
        },
      });
  }

  public resetName() {
    const inputSearchRef = this.select.element.getElementsByTagName('input');
    inputSearchRef[0].value = '';
  }
}
